import { useDispatch, useSelector } from 'react-redux';

import { useEffect, useRef } from 'react';
import { RootState } from '../ducks/store';
import { useGetTeamsSlice } from './useGetTeamsSlice';
import { useGetCourseNames } from './useGetCourseNames';
import { getCourseIdsFromCourseNames } from '../utils/reports';

/**
 * Fetches courses and stores active course names and course ids for selected teams to the Redux store on load
 */
export const useFetchCoursesOnLoad = () => {
  const initialCoursesFetch = useRef(false); // Used to prevent the execution of fetchCourses after the initial load

  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { activeTeams, activeDeselectedCourses } = useSelector(
    (state: RootState) => state.teamsSlice,
  );

  // ===========================================================================
  // Hooks
  // ===========================================================================

  const { fetchCourses } = useGetCourseNames();
  const { setActiveCourseNames, setCurrentTeamCourseIds } = useGetTeamsSlice();

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  // Fetch courses and store active course names and course ids for selected teams to the Redux store
  useEffect(() => {
    if (initialCoursesFetch.current) {
      return;
    }

    fetchCourses({ teamIds: activeTeams }).then(({ data }) => {
      const courseIds = getCourseIdsFromCourseNames(data);
      const courseNames =
        data
          ?.filter((course) => !activeDeselectedCourses.includes(course._id))
          .map((course) => course.title) || [];

      setCurrentTeamCourseIds(courseIds);
      setActiveCourseNames(courseNames);

      initialCoursesFetch.current = true;
    });

    // We don't want to run this effect every time the setter functions change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeams, activeDeselectedCourses, dispatch]);
};
